import { type SlotAlignment } from '@blaze-runtime/engine-domain';
import { isDatasetLinkValue } from '@blaze-shared/schema-domain';
import { isDeepEqual, isNullish } from '@blaze-shared/shared-util';

export function isFormListWrapping(
  inputs: boolean | HTMLDivElement[],
  alignment: SlotAlignment['horizontal'] | undefined,
): boolean {
  if (typeof inputs === 'boolean' || !inputs?.length) {
    return false;
  }
  let isWrapping = false;

  if (!alignment || alignment === 'left') {
    const x = inputs[0].offsetLeft;
    for (let i = 1; i < inputs.length; i++) {
      if (inputs[i].offsetLeft === x) {
        isWrapping = true;
        break;
      }
    }
  } else if (alignment === 'center') {
    let maxH = 0;
    let maxY = Number.POSITIVE_INFINITY;
    let maxYH = 0;
    for (const input of inputs) {
      if (input.offsetHeight > maxH && input.offsetTop <= maxY) {
        maxH = input.offsetHeight;
        maxY = input.offsetTop;
        maxYH = input.offsetTop + input.offsetHeight;
      } else if (input.offsetTop >= maxYH) {
        isWrapping = true;
        break;
      }
    }
  } else if (alignment === 'right') {
    let maxX = 0;
    let maxCount = 0;
    for (const input of inputs) {
      const iX = input.offsetLeft + input.offsetWidth;
      if (iX > maxX) {
        maxX = iX;
        maxCount = 1;
      } else if (iX === maxX) {
        maxCount++;
      }
    }
    if (maxCount > 1) {
      isWrapping = true;
    }
  }

  return isWrapping;
}

/**
 * checks equality ignoring nullish values.
 * if one of the values is nullish, a default value,
 * derived from the other's value type, will be used for comparison
 * also handles switches from an id to a dataset link object
 */
export function isSoftEqual(x: any, y: any): boolean {
  if (!isNullish(x) && !isNullish(y)) {
    if (typeof x === 'string' && typeof y === 'object' && isDatasetLinkValue(y)) {
      y = y.rowId;
    } else if (typeof y === 'string' && typeof x === 'object' && isDatasetLinkValue(x)) {
      x = x.rowId;
    }

    return isDeepEqual(x, y);
  }

  let def;
  if (typeof x === 'number' || typeof y === 'number') {
    def = 0;
  } else if (typeof x === 'boolean' || typeof y === 'boolean') {
    def = false;
  } else if (typeof x === 'string' || typeof y === 'string') {
    def = '';
  }

  if (isNullish(x)) {
    x = def;
  }
  if (isNullish(y)) {
    y = def;
  }
  return isDeepEqual(x, y);
}

export function getFirstRowInputs(
  host: HTMLDivElement,
  expectedFieldsCount: number,
): HTMLDivElement[] | undefined {
  const firstRow = host.querySelector('div.form-list-row');
  if (!firstRow) {
    return undefined;
  }

  const inputs = firstRow.querySelectorAll<HTMLDivElement>(
    '.blaze-target.blaze-element:not(.type-breakLine,.type-container)',
  );
  if (inputs?.length < expectedFieldsCount) {
    return undefined;
  }

  return Array.from(inputs);
}
